import { LitElement, html } from "lit";
import { customElement, state } from "lit/decorators.js";
import { effect } from "@preact/signals-core";
import {
  productData,
  selectedVariant,
  isAvailable,
  shipsBy,
} from "../store/product-store";
import { calculateShippingDate, scrollToVariantPicker } from "../utils/knocking";
import { isCartLoading, getLimitStatus } from "../store/cart-store";
import { isExtendedOfferEnabled } from "../store/extended-offer-store";

type ButtonState = "add-to-cart" | "extended-offer" | "limit-reached";

@customElement("sticky-add-to-cart")
export class StickyAddToCart extends LitElement {
  @state() private _productData: any = null;
  @state() private _variant: any = null;
  @state() private _available = false;
  @state() private _isVisible = false;
  @state() private _limitReached = false;
  @state() private _extendedOfferEnabled = false;
  @state() private _loading = false;
  @state() private _shippingDate: string | null = null;

  private _mainButton: HTMLElement | null = null;
  private _footer: HTMLElement | null = null;
  private _scrollHandler: (() => void) | null = null;
  private _resizeHandler: (() => void) | null = null;

  constructor() {
    super();

    // Subscribe to all relevant signals
    effect(() => {
      this._productData = productData.value;
      this._variant = selectedVariant.value;
      this._available = isAvailable.value;
      this._extendedOfferEnabled = Boolean(isExtendedOfferEnabled.value);
      this._loading = isCartLoading.value;

      // Check limit status
      if (productData.value) {
        const status = getLimitStatus(
          productData.value.id,
          productData.value.vendor ?? ""
        );
        this._limitReached = status.reached;
      }

      // Calculate shipping date
      const shipsByTag = shipsBy.value;
      if (shipsByTag) {
        try {
          const calculated = calculateShippingDate(shipsByTag);
          this._shippingDate = calculated.shortDate || calculated.longDate;
        } catch (error) {
          console.error("sticky-add-to-cart: failed to calculate shipping date", error);
          this._shippingDate = null;
        }
      } else {
        this._shippingDate = null;
      }

    });
  }

  connectedCallback() {
    super.connectedCallback();

    // Prefer the dark <footer> so clearance keeps the footer background
    this._footer =
      document.querySelector("#shopify-section-footer footer") ||
      document.querySelector("#shopify-section-footer");

    // Delay finding the button to ensure the page is fully loaded
    // This is needed because the main add-to-cart component might mount after us
    setTimeout(() => {
      // Find main add-to-cart button
      this._mainButton = document.querySelector(".product-form__submit");

      if (!this._mainButton) {
        console.warn("[sticky-add-to-cart] Main button not found");
        return;
      }

      // Setup scroll and resize handlers
      this._scrollHandler = this._checkVisibility.bind(this);
      this._resizeHandler = () => {
        this._checkVisibility();
        this._syncFooterPadding();
      };

      // Add event listeners with passive scroll for performance
      window.addEventListener("scroll", this._scrollHandler, { passive: true });
      window.addEventListener("resize", this._resizeHandler);

      // Initial visibility check
      this._checkVisibility();
      this._syncFooterPadding();
    }, 100);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Cleanup event listeners
    if (this._scrollHandler) {
      window.removeEventListener("scroll", this._scrollHandler);
    }
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
    }

    // Remove sticky-bar clearance from the footer
    if (this._footer) {
      this._footer.style.paddingBottom = "";
    }
  }

  protected updated(changedProperties: Map<string, unknown>): void {
    super.updated(changedProperties);
    this._syncFooterPadding();
  }

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this; // Light DOM - allows Tailwind classes to work
  }

  private _checkVisibility() {
    if (!this._mainButton) return;

    const rect = this._mainButton.getBoundingClientRect();
    // Show sticky cart when main button scrolls out of view (60px threshold)
    const isVisible = rect.bottom < 0 || rect.bottom < 60;

    if (isVisible !== this._isVisible) {
      this._isVisible = isVisible;
    }
  }

  /**
   * Reserve space at the bottom of the footer so logo/legal links can scroll
   * above the fixed sticky ATC bar (bar stays visible).
   */
  private _syncFooterPadding() {
    if (!this._footer) return;

    const bar = this.firstElementChild as HTMLElement | null;
    if (!bar) {
      this._footer.style.paddingBottom = "";
      return;
    }

    // Exact sticky bar height only — keeps logo/legal flush above the bar
    const height = Math.round(bar.getBoundingClientRect().height);
    this._footer.style.paddingBottom = height > 0 ? `${height}px` : "";
  }

  private _handleAddToCart(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    if (this._loading || !this._mainButton) return;

    // Delegate to main add-to-cart button
    // This maintains all existing logic: validation, modal chains, extended offers, etc.
    this._mainButton.click();
  }

  private _handleChange(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    // Product page: scroll the variant picker to the bottom of the viewport.
    // Any other page: no-op.
    scrollToVariantPicker();
  }

  private get _buttonState(): ButtonState {
    if (!this._available && this._extendedOfferEnabled) return "extended-offer";
    if (this._limitReached) return "limit-reached";
    return "add-to-cart";
  }

  private _renderSpinner() {
    return html`
      <div class="flex items-center justify-center w-full">
        <svg
          aria-hidden="true"
          focusable="false"
          role="presentation"
          class="animate-spin"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
        >
          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" stroke-width="2.5"></circle>
          <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.9)" stroke-width="2.5"
            stroke-linecap="round" stroke-dasharray="62.83" stroke-dashoffset="47"></circle>
        </svg>
      </div>
    `;
  }

  render() {
    if (!this._productData) return html``;

    // Visibility transition classes
    const visibilityClasses = this._isVisible
      ? "opacity-100 translate-y-0"
      : "opacity-0 translate-y-full pointer-events-none";

    // Button configuration based on state
    const state = this._buttonState;
    const buttonConfig = {
      "add-to-cart": { label: "Add to cart", styleOverride: "" },
      "extended-offer": {
        label: "Notify Me",
        styleOverride:
          "bg-[var(--color-secondary-500)] hover:bg-[var(--color-secondary-500)] text-white",
      },
      "limit-reached": { label: "Add to cart", styleOverride: "" },
    };

    const buttonLabel = buttonConfig[state].label;
    const buttonStyleOverride = buttonConfig[state].styleOverride;
    const isDisabled = state !== "extended-offer" && this._limitReached || this._loading;
    const showSoldOutShipsBy = state === "extended-offer";
    const showShipsByPanel = showSoldOutShipsBy || Boolean(this._shippingDate);

    // Get image source (variant image or product image), normalise protocol-relative URLs
    const rawImageSrc =
      this._variant?.featured_image?.src ||
      this._productData?.featured_image ||
      "";
    const imageSrc = rawImageSrc.startsWith("//") ? `https:${rawImageSrc}` : rawImageSrc;
    const imageAlt =
      this._variant?.featured_image?.alt || this._productData.title;

    const hasMultipleVariants =
      (this._productData.variants?.length ?? 0) > 1 ||
      this._productData.has_only_default_variant === false;

    return html`
      <div
        class="fixed bottom-0 left-0 right-0 z-10 bg-gradient-to-b from-[var(--color-secondary-50)] to-[var(--color-secondary-100)] shadow-lg transition-all duration-300 ease-in-out ${visibilityClasses}"
      >
        <div class="w-full px-4 py-3">
          <div class="flex align-center lg:hidden w-full gap-3">
            ${imageSrc
              ? html`
                  <img
                    src="${imageSrc}"
                    alt="${imageAlt}"
                    class="w-22 h-22 object-cover rounded-sm bg-white flex-wrap"
                  />
                `
              : html``}

            <div class="flex min-w-0 flex-1 flex-col gap-2">
              <div class="min-w-0">
                <div class="text-caption">${this._productData.vendor}</div>
                <div class="truncate text-base font-bold">${this._productData.title}</div>
              </div>

              <div
                class="flex min-w-0 flex-wrap items-center gap-3 ${!hasMultipleVariants
                  ? "hidden"
                  : ""}"
              >
                <div class="flex items-center gap-2 flex-wrap">
                  <div class="border border-[var(--color-secondary-500)] rounded-sm px-4 py-2 flex items-center justify-center">
                    <span class="text-xs font-bold text-[var(--color-secondary-500)] uppercase">
                      ${this._variant?.options?.join(" / ") ?? ""}
                    </span>
                  </div>
                  <button
                    @click="${this._handleChange}"
                    class="text-xs font-bold text-[var(--color-secondary-500)] uppercase hover:opacity-80 underline tracking-wide"
                  >
                    Change
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3 flex lg:hidden w-full items-center gap-2 w-full">
            <div class="flex min-w-0 items-center gap-2 flex-wrap w-full justify-between">
              <price-pp compact></price-pp>
              <badges-pp></badges-pp>
            </div>
          </div>

          <button
            @click="${this._handleAddToCart}"
            ?disabled="${isDisabled}"
            class="btn ${buttonStyleOverride} mt-3 flex lg:hidden w-full min-h-[48px] ${isDisabled
              ? "opacity-50 cursor-not-allowed"
              : ""}"
          >
            ${this._loading
              ? this._renderSpinner()
              : html`<span>${buttonLabel}</span>`}
          </button>

          <div class="hidden lg:flex lg:items-center lg:gap-6 lg:justify-between">
            ${imageSrc
              ? html`
                  <img
                    src="${imageSrc}"
                    alt="${imageAlt}"
                    class="md:w-[95px] md:h-[95px] object-cover rounded-sm bg-white flex-shrink-0 bg-white"
                  />
                `
              : html``}

            <div class="flex flex-1 min-w-0 items-center justify-between gap-6">
              <div class="flex min-w-0 flex-1 flex-wrap items-center gap-4">
                <div class="flex min-w-0 flex-col gap-1">
                  <div class="text-caption">${this._productData.vendor}</div>
                  <div class="truncate text-lg font-bold">${this._productData.title}</div>
                </div>
                ${hasMultipleVariants
                  ? html`
                      <div class="flex flex-wrap items-center gap-2">
                        <div class="flex min-h-[51px] min-w-[79px] items-center justify-center rounded-sm border border-[var(--color-secondary-500)] md:px-6 md:py-[17px]">
                          <span class="text-xs font-bold uppercase text-[var(--color-secondary-500)]">
                            ${this._variant?.options?.join(" / ") ?? ""}
                          </span>
                        </div>
                        <button
                          @click="${this._handleChange}"
                          class="text-xs font-bold uppercase tracking-wide text-[var(--color-secondary-500)] underline hover:opacity-80"
                        >
                          Change
                        </button>
                      </div>
                    `
                  : html``}
              </div>

              <div class="flex items-center gap-4 min-w-0 flex-wrap">
                <div class="flex items-center gap-2 flex-wrap">
                  <price-pp compact></price-pp>
                  <badges-pp></badges-pp>
                </div>

                <div class="flex overflow-hidden rounded-[5px]">
                  ${showSoldOutShipsBy
                    ? html`
                      <div
                        class="bg-[var(--color-secondary-50)] uppercase text-xs text-[var(--color-primary-950)] tracking-[0.1em] py-[5.5px] px-4 text-center flex flex-col justify-center items-center"
                      >
                        <span class="text-sm font-bold text-[var(--color-secondary-950)]">Sold Out</span>
                      </div>
                    `
                    : this._shippingDate
                    ? html`
                      <div
                        class="bg-[var(--color-primary-50)] uppercase text-xs text-[var(--color-primary-950)] tracking-[0.1em] py-[5.5px] px-4 text-center flex flex-col justify-center items-center"
                      >
                        <span class="text-xs text-[var(--color-primary-950)] uppercase">Ships by</span>
                        <span class="text-sm font-bold text-[var(--color-primary-950)]">${this._shippingDate}</span>
                      </div>
                    `
                    : html``
                  }
                  <button
                    @click="${this._handleAddToCart}"
                    ?disabled="${isDisabled}"
                    class="btn ${buttonStyleOverride} ${showShipsByPanel ? 'rounded-l-none' : ''} min-h-[60px] min-w-[150px] ${isDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : ""}"
                  >
                    ${this._loading
                      ? this._renderSpinner()
                      : html`<span>${buttonLabel}</span>`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
}
